<template>
  <div class="scada-dashboard">
    <div class="dashboard-container">
      <!-- 产线列表视图 -->
      <production-line-list
        v-if="viewMode === 'list'"
        :production-lines="productionLines"
        :loading="loading"
        :error="error"
        :user-name="userName"
        @select-line="selectProductionLine"
      />
      
      <!-- 产线详情视图 -->
      <template v-else-if="viewMode === 'detail'">
        <!-- 顶部栏 -->
        <top-bar :user-name="dashboardData.userName" class="load-animation" />
        
        <!-- 信息栏 -->
        <info-bar 
          v-if="dashboardData.workOrder" 
          :work-order="dashboardData.workOrder" 
          class="load-animation delay-1"
        >
          <template #extra>
            <button @click="backToList" class="back-button">
              <i class="el-icon-back"></i>
              返回产线列表
            </button>
          </template>
        </info-bar>
        
        <!-- 主内容区 -->
        <div class="main-content load-animation delay-2">
          <!-- 左侧区域 -->
          <div class="left-section">
            <!-- 上方：生产进度 + 设备运行状态 -->
            <div class="top-section">
              <progress-module 
                v-if="dashboardData.workOrder"
                :plan-quantity="dashboardData.workOrder.planQuantity"
                :collected-quantity="dashboardData.workOrder.collectedQuantity"
                :plan-unit="dashboardData.workOrder.planUnit"
                :plan-unit-name="dashboardData.workOrder.planUnitName"
                class="progress-wrapper"
              />
              <gauge-module 
                v-if="dashboardData.machines && dashboardData.machines.length > 0"
                :machines="dashboardData.machines"
                class="gauge-wrapper"
              />
            </div>
            
            <!-- 下方：工控机卡片 -->
            <div class="bottom-section">
              <machine-card
                v-for="machine in dashboardData.machines"
                :key="machine.machineCode"
                :machine="machine"
              />
            </div>
          </div>
          
          <!-- 右侧：预警信息 -->
          <div class="right-section">
            <alarm-module 
              v-if="dashboardData.machines"
              :machines="dashboardData.machines" 
            />
          </div>
        </div>
        
        <!-- 加载状态 -->
        <div v-if="loading && !dashboardData.workOrder" class="loading-overlay">
          <div class="loading-spinner"></div>
          <div class="loading-text">数据加载中...</div>
        </div>
        
        <!-- 错误提示 -->
        <div v-if="error" class="error-overlay">
          <div class="error-icon">⚠</div>
          <div class="error-message">{{ error }}</div>
          <div class="error-actions">
            <button @click="fetchDetailData" class="retry-button">重试</button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getScadaDashboard } from '@/api/scada'
import TopBar from './components/TopBar.vue'
import InfoBar from './components/InfoBar.vue'
import ProgressModule from './components/ProgressModule.vue'
import GaugeModule from './components/GaugeModule.vue'
import MachineCard from './components/MachineCard.vue'
import AlarmModule from './components/AlarmModule.vue'
import ProductionLineList from './components/ProductionLineList.vue'

export default {
  name: 'ScadaDashboard',
  components: {
    TopBar,
    InfoBar,
    ProgressModule,
    GaugeModule,
    MachineCard,
    AlarmModule,
    ProductionLineList
  },
  data() {
    return {
      viewMode: 'list', // 'list' 或 'detail'
      selectedWorklineCode: '', // 选中的产线号
      productionLines: [], // 产线列表数据
      userName: '系统用户', // 用户名
      dashboardData: {
        userName: '',
        workOrder: null,
        machines: []
      },
      loading: false,
      error: null,
      timer: null,
      resizeTimer: null
    }
  },
  mounted() {
    // 从路由参数获取产线号
    const worklineCode = this.$route.query.worklineCode
    
    if (worklineCode) {
      // 如果有产线号，直接进入详情页
      this.selectedWorklineCode = worklineCode
      this.viewMode = 'detail'
      this.fetchDetailData()
      this.startDetailTimer()
    } else {
      // 否则显示产线列表
      this.viewMode = 'list'
      this.fetchListData()
      this.startListTimer()
    }
    
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.stopTimer()
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
  },
  methods: {
    // 获取产线列表数据
    async fetchListData() {
      try {
        this.loading = true
        this.error = null
        
        // 不传产线号，获取所有产线数据
        const response = await getScadaDashboard()
        
        // 后端返回的是 data.worklines 数组
        this.productionLines = response.data?.worklines || []
        this.userName = response.data?.userName || '系统用户'
      } catch (err) {
        this.error = err.message || '获取产线列表失败'
        console.error('获取产线列表失败:', err)
      } finally {
        this.loading = false
      }
    },
    
    // 获取产线详情数据
    async fetchDetailData() {
      try {
        this.loading = true
        this.error = null
        
        const response = await getScadaDashboard(this.selectedWorklineCode)
        
        this.dashboardData = response.data
        // 更新用户名
        if (response.data.userName) {
          this.userName = response.data.userName
        }
      } catch (err) {
        this.error = err.message || '获取产线详情失败'
        console.error('获取产线详情失败:', err)
      } finally {
        this.loading = false
      }
    },
    
    // 选择产线，进入详情页
    selectProductionLine(worklineCode) {
      this.selectedWorklineCode = worklineCode
      this.viewMode = 'detail'
      
      // 更新路由参数
      this.$router.push({
        query: { worklineCode }
      })
      
      // 停止列表定时器，启动详情定时器
      this.stopTimer()
      this.fetchDetailData()
      this.startDetailTimer()
    },
    
    // 返回产线列表
    backToList() {
      this.viewMode = 'list'
      this.selectedWorklineCode = ''
      
      // 清除路由参数
      this.$router.push({
        query: {}
      })
      
      // 停止详情定时器，启动列表定时器
      this.stopTimer()
      this.fetchListData()
      this.startListTimer()
    },
    
    // 启动列表定时器（每5秒刷新）
    startListTimer() {
      this.timer = setInterval(() => {
        this.fetchListData()
      }, 5000)
    },
    
    // 启动详情定时器（每5秒刷新）
    startDetailTimer() {
      this.timer = setInterval(() => {
        this.fetchDetailData()
      }, 5000)
    },
    
    // 停止定时器
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    
    handleResize() {
      // 防抖处理resize事件
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      
      this.resizeTimer = setTimeout(() => {
        // 触发图表重绘
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'))
        })
      }, 300)
    }
  }
}
</script>

<style lang="scss" scoped>
.scada-dashboard {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%);
  overflow: hidden;
  position: relative;
  
  // 科技风格背景网格
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(0, 255, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 255, 255, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
  }
}

.dashboard-container {
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  display: flex;
  gap: 20px;
  margin-top: 20px;
  overflow: hidden;
}

.left-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
}

.top-section {
  height: 35%;
  min-height: 280px;
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.progress-wrapper {
  width: 30%;
  min-width: 280px;
}

.gauge-wrapper {
  flex: 1;
  min-width: 400px;
}

.bottom-section {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  overflow-y: auto;
  padding-right: 10px;
  padding-top: 10px;
  
  // 自定义滚动条
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(0, 255, 255, 0.5);
    }
  }
}

.right-section {
  width: 20%;
  min-width: 260px;
  flex-shrink: 0;
}

// 加载状态
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 14, 39, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(0, 255, 255, 0.1);
  border-top-color: #00ffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 20px;
  color: #00ffff;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(20, 10, 10, 0.95);
  border: 2px solid rgba(255, 0, 0, 0.5);
  border-radius: 8px;
  padding: 40px;
  z-index: 1000;
  text-align: center;
  min-width: 400px;
  box-shadow: 0 0 30px rgba(255, 0, 0, 0.3);
}

.error-icon {
  font-size: 48px;
  color: #ff4444;
  margin-bottom: 20px;
  animation: pulse 2s ease-in-out infinite;
}

.error-message {
  color: #ff4444;
  font-size: 16px;
  margin-bottom: 20px;
  text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
  line-height: 1.6;
}

.error-actions {
  margin-top: 20px;
}

.retry-button {
  background: linear-gradient(135deg, rgba(255, 0, 0, 0.2), rgba(255, 0, 0, 0.1));
  border: 1px solid rgba(255, 0, 0, 0.5);
  color: #ff4444;
  padding: 10px 30px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 0, 0, 0.3), rgba(255, 0, 0, 0.2));
    box-shadow: 0 0 15px rgba(255, 0, 0, 0.5);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
}

// 页面加载动画
@keyframes pageLoadSequence {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.load-animation {
  animation: pageLoadSequence 0.6s ease-out forwards;
  opacity: 0;
  
  &.delay-1 {
    animation-delay: 0.1s;
  }
  
  &.delay-2 {
    animation-delay: 0.2s;
  }
  
  &.delay-3 {
    animation-delay: 0.3s;
  }
}

// 返回按钮样式
.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(0, 255, 255, 0.1));
  border: 1px solid rgba(0, 255, 255, 0.5);
  color: #00ffff;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
  
  i {
    font-size: 16px;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(0, 255, 255, 0.2));
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
}
</style>
